import { Component, OnInit, OnDestroy, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { KitDetailsService } from '../../../shared/services/apis/platform/kit-details.service';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { KitDetailsResponse, RatingInfo } from '../../../shared/services/responses/platform/kit-details.response';
import { CEPCorreios, UtilService } from '../../../shared/services/util.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { EmptyListComponent } from "../../../shared/components/empty-list/empty-list.component";
import { LoadingBoxComponent } from "../../../shared/components/loading-box/loading-box.component";
import { KitDetailsCorreiosEstimateResponse } from '../../../shared/services/responses/platform/kit-details-correios-estimate.response';
import { AuthService } from '../../../shared/services/apis/platform/auth.service';
import { ShoppingCartService } from '../../../shared/services/apis/platform/shopping-cart.service';
import { BookVersionViewService } from '../../../shared/services/apis/platform/book-version-view.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginComponent } from '../../../shared/components/login/login.component';

@Component({
  selector: 'app-kit-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MenuComponent,
    FooterComponent,
    CurrencyPipe,
    DatePipe,
    NgxMaskDirective,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    LoadingBoxComponent
  ],
  providers: [provideNgxMask()],
  templateUrl: './kit-details.component.html',
  styleUrls: ['./kit-details.component.css']
})
export class KitDetailsComponent implements OnInit, OnDestroy {
  public kitDetails: KitDetailsResponse | null = null;
  public kitDetailsCorreiosEstimateResponse: KitDetailsCorreiosEstimateResponse | null = null;
  public isLoading = true;
  public quantity = 1;
  public cep = '';
  public shippingInfo: { type: string, price: number, deliveryTime: number }[] | null = null;
  public isLoadingShipping = false;
  @ViewChild('carouselContainer') carouselContainer: ElementRef<HTMLElement>;
  @ViewChild(MenuComponent) private menuComponent!: MenuComponent;

  public idKit: number;
  private destroy$ = new Subject<void>();
  public address: string = "Digite seu CEP";

  public searchTerm: string;

  constructor(
    private route: ActivatedRoute,
    private kitDetailsService: KitDetailsService,
    private alertService: AlertService,
    private authService: AuthService,
    private shoppingCartService: ShoppingCartService,
    private bookVersionViewService: BookVersionViewService,
    private utilService: UtilService, // Utilizado para simular a busca de endereço
    public dialog: MatDialog,
    private router: Router,
  ) { }

  ngOnInit(): void {
    const idKitParam = this.route.snapshot.paramMap.get('idKit');

    if (idKitParam) {
      this.idKit = +idKitParam;
      this.fetchKitDetails();
    } else {
      this.isLoading = false;
      this.alertService.show('Erro', 'ID do kit não fornecido.', AlertType.error);
    }

    this.route.queryParams
      .subscribe(params => {
        this.searchTerm = params['termo'] || '';
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (this.menuComponent)
      this.menuComponent.searchFormControl.setValue(this.searchTerm);
  }

  private fetchKitDetails(): void {
    this.isLoading = true;
    let zipcode: string | null = null
    if (this.authService.isAuthenticated()) {
      zipcode = this.authService.getUserInfos().zipcode
    }


    this.kitDetailsService.get(this.idKit, zipcode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
          } else {
            this.kitDetails = response;

            if (response.listCorreiosEstimates) {
              this.kitDetailsCorreiosEstimateResponse = new KitDetailsCorreiosEstimateResponse();
              this.kitDetailsCorreiosEstimateResponse.listCorreiosEstimates = response.listCorreiosEstimates;
            }

            if (response.userZipcode) {
              this.cep = response.userZipcode
            }
          }
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.alertService.show('Erro', 'Não foi possível carregar os detalhes do livro.', AlertType.error);
        }
      });
  }

  calculateShipping(): void {
    if (!this.cep || this.cep.replace(/\D/g, '').length !== 8) {
      this.alertService.show('Atenção', 'Por favor, insira um CEP válido.', AlertType.warning);
      return;
    }

    this.isLoadingShipping = true;
    this.shippingInfo = null;

    this.kitDetailsService.getCorreios(this.idKit, this.cep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
          } else {
            this.kitDetailsCorreiosEstimateResponse = response;
          }
          this.isLoadingShipping = false;
        },
        error: () => {
          this.isLoadingShipping = false;
          this.alertService.show('Erro', 'Não foi possível carregar os dados dos correios.', AlertType.error);
        }
      });

    this.utilService.getAddressByCEP(this.cep).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cepCorreios: CEPCorreios) => {
          this.address = `${cepCorreios.logradouro}, ${cepCorreios.bairro}. ${cepCorreios.localidade} - ${cepCorreios.uf}. ${cepCorreios.cep}`

        },
        error: () => {
          this.alertService.show('Erro', 'Não foi possível carregar os dados dos correios.', AlertType.error);
        }
      });


  }

  incrementQuantity(): void {
    this.quantity++;
  }

  decrementQuantity(): void {
    if (this.quantity > 1) {
      this.quantity--;
    }
  }

  getRatingPercentage(stars: number): number {
    if (!this.kitDetails?.rating) return 0;

    const rating = this.kitDetails.rating;
    const totalRatings =
      rating.countRating1Star +
      rating.countRating2Star +
      rating.countRating3Star +
      rating.countRating4Star +
      rating.countRating5Star;

    if (totalRatings === 0) return 0;

    let count = 0;
    switch (stars) {
      case 5: count = rating.countRating5Star; break;
      case 4: count = rating.countRating4Star; break;
      case 3: count = rating.countRating3Star; break;
      case 2: count = rating.countRating2Star; break;
      case 1: count = rating.countRating1Star; break;
    }

    return (count / totalRatings) * 100;
  }

  scrollLeft(): void {
    const container = this.carouselContainer.nativeElement;
    // O valor do scroll é baseado na largura de um card + gap
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }

  scrollRight(): void {
    const container = this.carouselContainer.nativeElement;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }


  clickToBuy() {
    if (!this.authService.isAuthenticated()) {
      this.openModalLogin();
      return;
    }

    this.bookVersionViewService.RegisterBuyClick(this.idKit).subscribe({
      error: () => { } // silencioso
    });

    this.addCart(true, this.idKit);
  }


  addCartClick(idKit: number) {
    this.bookVersionViewService.RegisterAddToCart(idKit).subscribe({
      error: () => { } // silencioso
    });

    this.addCart(false, idKit);
  }

  addCart(redirectCheckout: boolean, idKit: number) {
    this.shoppingCartService.updateQuantity(idKit, this.quantity)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.isError) {
            this.alertService.show('Erro', response.errorDescription, AlertType.error);
          }

          this.alertService.show('Sucesso', "Carrinho atualizado", AlertType.success);
          this.menuComponent.verifyCart();

          if (redirectCheckout) {
            this.router.navigate(['/comprar']);
          }
        },
        error: () => {
          this.isLoadingShipping = false;
          this.alertService.show('Erro', 'Não foi possível carregar os dados dos correios.', AlertType.error);
        }
      });
  }

  openModalLogin() {
    this.dialog.open(LoginComponent, {
      data: {},
    });
  }
}